/*
 * Capital Raise Readiness Assessment — configuration
 * Version 1.0
 *
 * This file is the single source of truth for the assessment's
 * questions, weights, classification bands, diagnostic copy and
 * recommendation copy. It intentionally contains NO scoring logic
 * and NO DOM code (see capital-raise-assessment-scoring.js and
 * capital-raise-assessment-app.js) so the methodology can be revised
 * by editing data here without touching logic or presentation.
 *
 * Loaded as a UMD module: available as `window.ChadMCAssessmentConfig`
 * in the browser, and via `require()` under Node for the test suite.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.ChadMCAssessmentConfig = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var STANDARD_SCORES = { A: 0, B: 25, C: 50, D: 75, E: 100 };

  var CATEGORIES = {
    company_readiness: {
      key: 'company_readiness',
      label: 'Company Readiness',
      weight: 0.15,
      questionIds: ['q2', 'q3', 'q4']
    },
    investment_case: {
      key: 'investment_case',
      label: 'Investment Case',
      weight: 0.20,
      questionIds: ['q5', 'q6', 'q7', 'q8']
    },
    financing_strategy: {
      key: 'financing_strategy',
      label: 'Financing Strategy',
      weight: 0.20,
      questionIds: ['q9', 'q10', 'q11', 'q12', 'q13']
    },
    investor_materials: {
      key: 'investor_materials',
      label: 'Investor Materials',
      weight: 0.15,
      questionIds: ['q14', 'q15', 'q16', 'q17']
    },
    investor_targeting: {
      key: 'investor_targeting',
      label: 'Investor Targeting',
      weight: 0.15,
      questionIds: ['q18', 'q19', 'q20', 'q21']
    },
    raise_execution: {
      key: 'raise_execution',
      label: 'Raise Execution',
      weight: 0.15,
      questionIds: ['q22', 'q23', 'q24', 'q25', 'q26']
    }
  };

  /* Category display order, used consistently across progress UI and results. */
  var CATEGORY_ORDER = [
    'company_readiness',
    'investment_case',
    'financing_strategy',
    'investor_materials',
    'investor_targeting',
    'raise_execution'
  ];

  function opts(labels) {
    var letters = ['A', 'B', 'C', 'D', 'E'];
    return letters.map(function (letter, i) {
      return { value: letter, label: labels[i], score: STANDARD_SCORES[letter] };
    });
  }

  var QUESTIONS = [
    {
      id: 'q1',
      category: null,
      scored: false,
      dataField: 'company_stage',
      text: 'What stage is your company currently at?',
      helperText: 'This is for segmentation and context only — it does not affect your score.',
      options: [
        { value: 'A', label: 'Idea / pre-launch', dataValue: 'idea_prelaunch' },
        { value: 'B', label: 'Early development', dataValue: 'early_development' },
        { value: 'C', label: 'Operating, pre-revenue', dataValue: 'operating_prerevenue' },
        { value: 'D', label: 'Revenue generating', dataValue: 'revenue_generating' },
        { value: 'E', label: 'Established and growing', dataValue: 'established_growing' }
      ]
    },
    {
      id: 'q2',
      category: 'company_readiness',
      scored: true,
      text: 'How clearly can you explain what your company does and the problem or opportunity it addresses?',
      options: opts(['Not clearly yet', 'Somewhat', 'Clearly', 'Very clearly', 'Compellingly and consistently'])
    },
    {
      id: 'q3',
      category: 'company_readiness',
      scored: true,
      text: 'What evidence do you currently have that the business or opportunity works?',
      options: opts(['Very little', 'Early validation', 'Meaningful validation', 'Strong traction or milestones', 'Significant commercial or market validation'])
    },
    {
      id: 'q4',
      category: 'company_readiness',
      scored: true,
      text: 'Do you have a defined business plan and clear milestones for the next 12–24 months?',
      options: opts(['No', 'Roughly', 'Partially documented', 'Clearly documented', 'Detailed, measurable and actively managed'])
    },
    {
      id: 'q5',
      category: 'investment_case',
      scored: true,
      text: 'Can you clearly explain why this is an attractive investment, rather than simply a good business or project?',
      options: opts(['Not yet', 'Somewhat', 'Generally', 'Clearly', 'Extremely clearly'])
    },
    {
      id: 'q6',
      category: 'investment_case',
      scored: true,
      text: 'Can you explain why the opportunity matters now?',
      options: opts(['No clear catalyst', 'General timing argument', 'Several catalysts', 'Strong near-term catalysts', 'Compelling and demonstrable timing'])
    },
    {
      id: 'q7',
      category: 'investment_case',
      scored: true,
      text: 'How differentiated is your opportunity from alternatives available to investors?',
      options: opts(['Unclear', 'Some differentiation', 'Meaningful differentiation', 'Strong differentiation', 'Defensible or exceptional differentiation'])
    },
    {
      id: 'q8',
      category: 'investment_case',
      scored: true,
      text: "Can you articulate the major value-creation milestones investors' capital will help unlock?",
      options: opts(['Not yet', 'Broadly', 'Some milestones', 'Clear milestones', 'Clear milestones with timing and capital requirements'])
    },
    {
      id: 'q9',
      category: 'financing_strategy',
      scored: true,
      text: 'Do you know exactly how much capital you need to raise?',
      options: opts(['No', 'Rough estimate', 'Range', 'Defined amount', 'Defined amount supported by detailed use of proceeds'])
    },
    {
      id: 'q10',
      category: 'financing_strategy',
      scored: true,
      text: 'Have you determined the appropriate financing structure?',
      options: opts(['No', 'Exploring options', 'Likely structure', 'Structure selected', 'Structure selected and terms developed'])
    },
    {
      id: 'q11',
      category: 'financing_strategy',
      scored: true,
      text: 'Is your valuation or pricing supported by a defensible rationale?',
      options: opts(['Not considered', 'Rough estimate', 'Some comparable or rationale work', 'Well supported', 'Thoroughly supported and market-tested'])
    },
    {
      id: 'q12',
      category: 'financing_strategy',
      scored: true,
      text: 'Do you have a detailed use of proceeds?',
      options: opts(['No', 'Broad categories', 'Preliminary budget', 'Detailed allocation', 'Detailed allocation tied directly to milestones'])
    },
    {
      id: 'q13',
      category: 'financing_strategy',
      scored: true,
      text: 'Do you understand how this financing affects existing shareholders, ownership and future financing requirements?',
      options: opts(['Not really', 'Somewhat', 'Generally', 'Clearly', 'Fully modeled'])
    },
    {
      id: 'q14',
      category: 'investor_materials',
      scored: true,
      text: 'How complete is your investor presentation or deck?',
      options: opts(['None', 'Early draft', 'Mostly complete', 'Investor-ready', 'Investor-tested and refined'])
    },
    {
      id: 'q15',
      category: 'investor_materials',
      scored: true,
      text: 'Do you have current financial information and forward-looking financial projections appropriate to your business?',
      options: opts(['No', 'Limited', 'Basic', 'Complete', 'Complete and professionally prepared or reviewed'])
    },
    {
      id: 'q16',
      category: 'investor_materials',
      scored: true,
      text: 'How prepared is your supporting due-diligence or data-room material?',
      options: opts(['Not started', 'Early collection', 'Partially organized', 'Mostly complete', 'Organized and investor-ready'])
    },
    {
      id: 'q17',
      category: 'investor_materials',
      scored: true,
      text: 'Are your financing materials consistent with one another?',
      options: opts(['Not applicable or not sure', 'Significant gaps', 'Mostly consistent', 'Consistent', 'Fully integrated around one investment thesis'])
    },
    {
      id: 'q18',
      category: 'investor_targeting',
      scored: true,
      text: 'Have you clearly defined the type of investor most appropriate for this financing?',
      options: opts(['No', 'Broadly', 'Somewhat', 'Clearly', 'Multiple defined investor profiles'])
    },
    {
      id: 'q19',
      category: 'investor_targeting',
      scored: true,
      text: 'Do you have a qualified target list of prospective investors?',
      helperText: 'Quantity alone does not create a strong score here — fit matters more (see the next question).',
      options: opts(['No list', 'Under 10', '10–25', '25–75', '75+ qualified prospects'])
    },
    {
      id: 'q20',
      category: 'investor_targeting',
      scored: true,
      text: 'How well matched are the investors on your list to your sector, stage, cheque size and financing type?',
      options: opts(['Unknown', 'Weak', 'Mixed', 'Good', 'Highly targeted'])
    },
    {
      id: 'q21',
      category: 'investor_targeting',
      scored: true,
      text: 'How much warm access do you have to those investors?',
      options: opts(['Almost none', 'A few relationships', 'Some warm pathways', 'Strong network', 'Extensive direct or warm access'])
    },
    {
      id: 'q22',
      category: 'raise_execution',
      scored: true,
      text: 'Have you established a structured process for investor outreach and follow-up?',
      options: opts(['No', 'Informal', 'Basic process', 'Structured process', 'Structured and actively managed pipeline'])
    },
    {
      id: 'q23',
      category: 'raise_execution',
      scored: true,
      text: 'Are you tracking investor conversations, follow-ups, objections and next steps?',
      options: opts(['No', 'Notes or email', 'Basic spreadsheet', 'CRM or system', 'Disciplined CRM/process'])
    },
    {
      id: 'q24',
      category: 'raise_execution',
      scored: true,
      text: 'How prepared are you to handle investor questions and objections?',
      options: opts(['Not prepared', 'Somewhat', 'Generally prepared', 'Very prepared', 'Tested and refined through investor conversations'])
    },
    {
      id: 'q25',
      category: 'raise_execution',
      scored: true,
      text: 'Have you already spoken with prospective investors about this financing?',
      helperText: "This is also useful context, not just a score — a 'no' here isn't a sign of weak preparation if the financing simply hasn't launched yet.",
      options: opts(['No', 'A few exploratory conversations', 'Active discussions', 'Multiple serious discussions', 'Diligence, terms or commitments underway'])
    },
    {
      id: 'q26',
      category: 'raise_execution',
      scored: true,
      text: 'Do you have a defined process for moving an interested investor from first conversation through diligence and closing?',
      options: opts(['No', 'Rough idea', 'Partially defined', 'Clearly defined', 'Proven or repeatable process'])
    }
  ];

  /* Q20 special rule: if Q20 <= 25, cap the Investor Targeting category score at 50. */
  var INVESTOR_TARGETING_FIT_QUESTION_ID = 'q20';
  var INVESTOR_TARGETING_FIT_CAP_THRESHOLD = 25;
  var INVESTOR_TARGETING_FIT_CAP_VALUE = 50;

  /* Readiness classification bands, evaluated on the rounded overall score. */
  var CLASSIFICATIONS = [
    {
      key: 'foundation_stage',
      min: 0,
      max: 39,
      label: 'Foundation Stage',
      description: 'Important elements of the financing foundation still need to be developed before aggressively approaching investors.'
    },
    {
      key: 'developing_your_raise',
      min: 40,
      max: 59,
      label: 'Developing Your Raise',
      description: 'The foundation is taking shape, but there are meaningful gaps in the financing process that deserve attention before broad investor outreach.'
    },
    {
      key: 'raise_ready_with_gaps',
      min: 60,
      max: 79,
      label: 'Raise Ready, With Gaps',
      description: 'Many of the core elements are in place. The opportunity now is to address the specific constraints most likely to create friction in the financing process.'
    },
    {
      key: 'raise_ready',
      min: 80,
      max: 100,
      label: 'Raise Ready',
      description: 'Many of the core elements of a professional financing process are in place. The focus now shifts increasingly toward refinement, execution and investor engagement.'
    }
  ];

  /* Hard constraint rules that can cap the classification below what the raw score implies. */
  var CLASSIFICATION_CAP_RULES = [
    { category: 'financing_strategy', belowScore: 40, capClassificationKey: 'developing_your_raise' },
    { category: 'investment_case', belowScore: 40, capClassificationKey: 'developing_your_raise' }
  ];

  /* Rule 3: below these thresholds, results must explicitly prioritize the foundation. */
  var FOUNDATION_PRIORITY_RULE = {
    categories: ['company_readiness', 'investment_case'],
    belowScore: 25,
    message: 'Before broad investor outreach, prioritize strengthening the foundation: sharpening the company itself and the investment case for it. Everything downstream in the financing process — materials, targeting and execution — works better once that foundation is solid.'
  };

  var DIAGNOSTIC_TIERS = [
    { key: 'foundation', min: 0, max: 39 },
    { key: 'developing', min: 40, max: 69 },
    { key: 'strong', min: 70, max: 100 }
  ];

  var DIAGNOSTIC_LIBRARY = {
    company_readiness: {
      foundation: 'The underlying business or opportunity may need additional definition, validation or milestone planning before the financing process becomes the primary focus. Strengthening the company foundation can make every subsequent part of the raise clearer and more credible.',
      developing: 'The company foundation is taking shape, but some elements could be better defined or demonstrated. Focus on sharpening the business case, evidence of progress and the milestones the next round of capital is intended to unlock.',
      strong: 'Company Readiness appears to be one of the stronger parts of the financing. The business or opportunity has meaningful definition and supporting evidence. The priority becomes translating that foundation into a compelling investment case and financing process.'
    },
    investment_case: {
      foundation: 'The business may have potential, but the investment case itself needs more development. Investors need to understand not only what the company does, but why the opportunity is compelling, differentiated and timely from an investment perspective.',
      developing: 'The core investment case is visible, but it could be made sharper. Focus on why this opportunity, why now, what differentiates it and what specific value-creation milestones new capital can unlock.',
      strong: 'The investment case appears relatively well developed. The next priority is ensuring that the financing structure, materials and investor targeting communicate that opportunity consistently and effectively.'
    },
    financing_strategy: {
      foundation: 'The financing itself needs more definition before broad investor outreach. The amount being raised, structure, pricing or valuation, use of proceeds and expected milestones should work together as one coherent financing strategy.',
      developing: 'The financing strategy is taking shape, but some elements remain underdeveloped. Tightening the raise amount, structure, use of proceeds, pricing logic and dilution implications can make the process significantly more coherent.',
      strong: 'The financing strategy appears relatively well defined. The focus should now shift toward ensuring the investment materials, investor targeting and execution process accurately reflect that strategy.'
    },
    investor_materials: {
      foundation: 'Your investor materials appear to be a meaningful constraint. Before scaling outreach, build the core materials investors need to understand and evaluate the opportunity efficiently and make sure they tell one consistent story.',
      developing: 'The major investor materials are taking shape, but they may not yet operate as a complete investor-ready package. Focus on consistency, supporting financial information and due-diligence readiness.',
      strong: 'Investor materials appear to be relatively well developed. The priority becomes using those materials effectively within a targeted and disciplined investor process.'
    },
    investor_targeting: {
      foundation: 'Your investor universe appears to be one of the biggest gaps in the financing process. Before increasing outreach, define who is actually appropriate for the opportunity based on sector, stage, financing structure, cheque size and investment mandate. More investor names do not necessarily mean a stronger financing process. Better fit matters.',
      developing: 'You have begun developing an investor universe, but there is room to improve its quality, fit or accessibility. Focus on narrowing the list toward investors whose mandate and behaviour align with the specific financing.',
      strong: 'Investor targeting appears to be one of the stronger parts of the financing process. The priority becomes converting that targeting advantage into disciplined outreach, follow-up and investor progression.'
    },
    raise_execution: {
      foundation: 'The financing may need a more structured execution process. Investor outreach works better when prospects, conversations, follow-ups, objections, diligence and next steps are managed as a pipeline rather than as isolated conversations.',
      developing: 'You have elements of an execution process in place, but it could become more systematic. Focus on pipeline management, follow-up discipline, objection handling and moving interested investors through defined next steps.',
      strong: 'Raise Execution appears relatively well developed. The focus becomes maintaining discipline, learning from investor feedback and progressing qualified interest efficiently through diligence and closing.'
    }
  };

  var RECOMMENDATION_LIBRARY = {
    company_readiness: [
      { title: 'Clarify the business and opportunity.', body: 'Make sure the company can be explained simply and consistently.' },
      { title: 'Strengthen evidence.', body: 'Identify the traction, validation, milestones or proof points that support the opportunity.' },
      { title: 'Define the next milestones.', body: 'Show what the next 12–24 months should accomplish and what capital is required to get there.' }
    ],
    investment_case: [
      { title: 'Define why this is investable.', body: 'Separate the investment proposition from the general business description.' },
      { title: 'Clarify why now.', body: 'Identify the catalysts, timing and market conditions that make the opportunity relevant today.' },
      { title: 'Connect capital to value creation.', body: 'Show what milestones the financing is intended to unlock.' }
    ],
    financing_strategy: [
      { title: 'Define the raise.', body: 'Determine the amount of capital required and why.' },
      { title: 'Tie use of proceeds to milestones.', body: 'Show specifically what the capital is intended to accomplish.' },
      { title: 'Model the structure.', body: 'Understand financing terms, pricing or valuation, dilution and future financing implications before broad outreach.' }
    ],
    investor_materials: [
      { title: 'Build the core investor package.', body: 'Create or complete the primary investor presentation and supporting materials.' },
      { title: 'Align the story.', body: 'Ensure the deck, financial information, financing terms and supporting materials communicate the same investment thesis.' },
      { title: 'Prepare for diligence.', body: 'Organize the information a serious investor will need to evaluate the opportunity.' }
    ],
    investor_targeting: [
      { title: 'Define the investor profile.', body: 'Identify the appropriate sector, stage, geography, cheque size, financing type and mandate.' },
      { title: 'Build a qualified investor universe.', body: 'Prioritize investor fit over list size.' },
      { title: 'Map warm pathways.', body: 'Identify potential introductions through directors, advisors, shareholders, industry participants and existing relationships.' }
    ],
    raise_execution: [
      { title: 'Build the pipeline.', body: 'Create a defined process for prospects, outreach, conversations and next steps.' },
      { title: 'Establish follow-up discipline.', body: 'Track every meaningful investor interaction and required action.' },
      { title: 'Define progression.', body: 'Know how an investor moves from introduction to meeting, diligence, terms and closing.' }
    ]
  };

  /*
   * Commercial routing.
   *
   * SPEC AMBIGUITY (flagged, not silently resolved — see section 12/14 of the
   * brief): section 12 says "course" is the default for overall scores 0–79
   * unless the advanced Strategy Session criteria apply, which leaves no
   * numeric room for the "nurture" route described in section 14 ("clearly
   * early-stage"). The thresholds below are an explicit, editable, best-guess
   * default — NOT an authoritative interpretation — and should be confirmed
   * with Chad before this route is relied on commercially:
   *   nurture  = overall score is in Foundation Stage (< nurtureMaxOverallScore)
   *              AND the respondent selected an early company_stage on Q1.
   *   strategy_session = the section 13 criteria, exactly as specified.
   *   course   = everything else in 0–79.
   */
  var ROUTES = {
    course: 'course',
    strategy_session: 'strategy_session',
    nurture: 'nurture'
  };

  var ROUTING_RULES = {
    nurtureMaxOverallScore: 39,
    nurtureCompanyStages: ['idea_prelaunch', 'early_development'],
    strategySession: {
      minOverallScore: 75,
      minFinancingStrategyScore: 65,
      minQ25Score: 50,
      minRaiseExecutionScore: 65
    }
  };

  var ROUTE_COPY = {
    course: {
      heading: 'BUILD YOUR CAPITAL-RAISING PROCESS',
      body: 'Your assessment identified where the gaps are. Raising Capital for Business gives you the framework, workbook and process for developing the major elements of your financing.',
      cta: 'Explore Raising Capital for Business',
      priceDisplay: '$397',
      url: '/products/raising-capital-for-business-online-course'
    },
    strategy_session: {
      heading: 'APPLY THE STRATEGY TO YOUR RAISE',
      body: 'If the core pieces are already in place and the challenge is applying them to your specific financing, a Strategy Session may be the more appropriate next step.',
      cta: 'Explore a Strategy Session',
      url: '/products/1-hour-strategy-call-chad-mcmillan',
      secondaryOffer: 'course'
    },
    nurture: {
      heading: 'KEEP BUILDING THE FOUNDATION',
      body: "You're still early — that's fine. Here are resources to help you build toward a stronger raise before you go deep on investor outreach.",
      cta: 'Read the Capital Raise Framework',
      url: '/blogs/chad-mcmillan/how-to-raise-capital-for-your-business-complete-process'
    }
  };

  /* Rule 5 override margin: Investor Targeting becomes Primary Constraint
     outright when it sits at least this many points below every other
     category, even though the plain lowest-score rule would already pick it
     in that situation — kept explicit so the "why" survives in the output. */
  var INVESTOR_TARGETING_OVERRIDE_MARGIN = 10;

  /* Rule 7: two lowest categories within this many points are presented as
     closely related rather than implying false precision about which is worse. */
  var NEAR_TIE_THRESHOLD = 3;

  var DISCLAIMER_TEXT = 'This assessment is provided for educational and informational purposes only. It is designed to help identify areas of capital-raising preparedness and does not constitute investment, financial, legal or securities advice, an offer or solicitation, or a guarantee that financing will be available or successfully completed.';

  var ASSESSMENT_VERSION = '1.0';

  var GA4_EVENTS = {
    view: 'chadmc:capital_assessment_view',
    start: 'chadmc:capital_assessment_start',
    sectionComplete: 'chadmc:capital_assessment_section_complete',
    complete: 'chadmc:capital_assessment_complete',
    leadSubmit: 'chadmc:capital_assessment_lead_submit',
    resultsView: 'chadmc:capital_assessment_results_view',
    courseClick: 'chadmc:capital_assessment_course_click',
    strategyClick: 'chadmc:capital_assessment_strategy_click',
    retake: 'chadmc:capital_assessment_retake'
  };

  return {
    ASSESSMENT_VERSION: ASSESSMENT_VERSION,
    STANDARD_SCORES: STANDARD_SCORES,
    CATEGORIES: CATEGORIES,
    CATEGORY_ORDER: CATEGORY_ORDER,
    QUESTIONS: QUESTIONS,
    INVESTOR_TARGETING_FIT_QUESTION_ID: INVESTOR_TARGETING_FIT_QUESTION_ID,
    INVESTOR_TARGETING_FIT_CAP_THRESHOLD: INVESTOR_TARGETING_FIT_CAP_THRESHOLD,
    INVESTOR_TARGETING_FIT_CAP_VALUE: INVESTOR_TARGETING_FIT_CAP_VALUE,
    CLASSIFICATIONS: CLASSIFICATIONS,
    CLASSIFICATION_CAP_RULES: CLASSIFICATION_CAP_RULES,
    FOUNDATION_PRIORITY_RULE: FOUNDATION_PRIORITY_RULE,
    INVESTOR_TARGETING_OVERRIDE_MARGIN: INVESTOR_TARGETING_OVERRIDE_MARGIN,
    NEAR_TIE_THRESHOLD: NEAR_TIE_THRESHOLD,
    DIAGNOSTIC_TIERS: DIAGNOSTIC_TIERS,
    DIAGNOSTIC_LIBRARY: DIAGNOSTIC_LIBRARY,
    RECOMMENDATION_LIBRARY: RECOMMENDATION_LIBRARY,
    ROUTES: ROUTES,
    ROUTING_RULES: ROUTING_RULES,
    ROUTE_COPY: ROUTE_COPY,
    DISCLAIMER_TEXT: DISCLAIMER_TEXT,
    GA4_EVENTS: GA4_EVENTS
  };
});
